import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'

/**
 * Type definition for sorting options.
 *
 * @typedef SortingType
 * @type {object}
 * @property {string} id - Unique identifier for the sorting option.
 * @property {string} label - Display label for the sorting option.
 */
type SortingType = {id: string; label: string}

interface SortingParameters {
    options: Array<SortingType>
}

/**
 * @name useSortUrls
 *
 * @hook
 *
 * @description
 * Custom hook to generate URLs with sorting parameters based on current location and provided options.
 *
 * @param {SortingParameters} params - Parameters for the hook.
 * @param {SortingType[]} params.options - Array of sorting options containing IDs and labels.
 *
 * @returns {Array<string>} Array of URLs with updated sorting parameters.
 *
 * @example
 * ```tsx
 * const options = [
 *   { id: 'name', label: 'Name' },
 *   { id: 'date', label: 'Date' }
 * ];
 * const sortedUrls = useSortUrls({ options });
 * ```
 */
export const useSortUrls = ({options = []}: SortingParameters): Array<string> => {
    const location = useLocation()

    /**
     * Updates search parameters with new values.
     *
     * @param searchParams - Current search parameters.
     * @param newParams - New parameters to be added or updated.
     */
    const updateSearchParams = (searchParams, newParams) => {
        Object.entries(newParams).forEach(([key, value]) => {
            // 0 is a valid value as for a param
            if (!value && value !== 0) {
                searchParams.delete(key)
            } else {
                searchParams.set(key, value)
            }
        })
    }

    /**
     * Rebuilds the URL with updated search parameters.
     *
     * @param {string} url - Original URL.
     * @param {object} extraParams - Additional parameters to include in the URL.
     * @returns {string} Updated URL with new search parameters.
     */
    const rebuildPathWithParams = (url: string, extraParams: object): string => {
        const [pathname, search] = url.split('?')
        const params = new URLSearchParams(search)

        updateSearchParams(params, extraParams)

        // Clean up any trailing `=` for params without values.
        const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '')

        // Generate the newly updated url.
        return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`
    }

    /**
     * Builds a set of URLs with specified sorting values and extra parameters.
     *
     * @param {string} url - Base URL.
     * @param {string} key - Parameter key for sorting.
     * @param {string[]} values - Array of values for sorting.
     * @param {object} extraParams - Additional parameters to include in each URL.
     * @returns {string[]} Array of URLs with updated sorting parameters.
     */
    const buildUrlSet = (url: string = '', key: string = '', values: string[] = [], extraParams = {}): Array<string> =>
        values.map((value) => rebuildPathWithParams(url, {[key]: value, ...extraParams}))

    return useMemo(
        () =>
            buildUrlSet(
                `${location.pathname}${location.search}`,
                'sort',
                options.map(({id}) => id),
                {
                    offset: 0,
                },
            ),
        [location.pathname, location.search, options],
    )
}
